import React, { useEffect, useMemo, useState, useCallback } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../App";
import API from "../utils/api";

const currentYear = new Date().getFullYear();

const TARGET_SAMPLE = `employee_email,target_year,period_type,period_value,institution_target,revenue_target,notes
divya.sales@ebek.in,2026,monthly,5,45,1800000,May target
naveen.sales@ebek.in,2026,quarterly,2,120,4500000,Q2 target
divya.sales@ebek.in,2026,yearly,1,480,18000000,Annual target`;

function parseCsv(text) {
  const lines = text.replace(/\r/g, "").split("\n").filter(line => line.trim());
  if (lines.length < 2) return [];
  const parseLine = (line) => {
    const cells = [];
    let cur = "";
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = !quoted;
      } else if (ch === "," && !quoted) {
        cells.push(cur.trim());
        cur = "";
      } else {
        cur += ch;
      }
    }
    cells.push(cur.trim());
    return cells;
  };
  const headers = parseLine(lines[0]).map(h => h.trim());
  return lines.slice(1).map(line => {
    const cells = parseLine(line);
    return headers.reduce((row, header, index) => {
      row[header] = cells[index] || "";
      return row;
    }, {});
  });
}

function money(value) {
  return Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function periodLabel(row) {
  if (row.period_type === "yearly") return `${row.target_year} Year`;
  if (row.period_type === "quarterly") return `Q${row.period_value} ${row.target_year}`;
  return `${new Date(row.target_year, Number(row.period_value || 1) - 1, 1).toLocaleString("en-IN", { month: "short" })} ${row.target_year}`;
}

export default function TargetCenter({ accent, managementView = false }) {
  const { user } = useAuth();
  // Role alone is not enough: managers/admins using the Self pill must see only
  // their own target. Management controls are enabled only by team/admin screens.
  const canManage = managementView && ["team_leader", "manager", "admin", "superadmin"].includes(user?.role);
  const [mode, setMode] = useState("targets");
  const [targetCsv, setTargetCsv] = useState(TARGET_SAMPLE);
  const [targets, setTargets] = useState([]);
  const [summary, setSummary] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [manual, setManual] = useState({
    employee_email: "",
    target_year: currentYear,
    period_type: "monthly",
    period_value: new Date().getMonth() + 1,
    institution_target: "",
    revenue_target: "",
    notes: "",
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // Individual .catch() so one failing endpoint doesn't block the rest
    const [targetsRes, summaryRes, employeesRes] = await Promise.all([
      API.get("/bulk-ops/targets", { params: { year: currentYear, scope: "self" } }).catch(() => null),
      API.get("/bulk-ops/target-summary", { params: { year: currentYear, scope: canManage ? "team" : "self" } }).catch(() => null),
      canManage ? API.get("/bulk-ops/employees").catch(() => null) : Promise.resolve(null),
    ]);
    if (targetsRes)   setTargets(targetsRes.data || []);
    if (summaryRes)   setSummary(summaryRes.data || []);
    if (employeesRes) {
      setEmployees(employeesRes.data || []);
      if (!manual.employee_email && employeesRes.data?.[0]) {
        setManual(m => ({ ...m, employee_email: employeesRes.data[0].email }));
      }
    }
    if (!targetsRes && !summaryRes) toast.error("Failed to load targets");
    setLoading(false);
  }, [manual.employee_email, canManage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!canManage) setMode("targets"); }, [canManage]);

  const targetRows = useMemo(() => {
    if (mode === "summary") return summary;
    const summaryById = new Map(summary.map(row => [row.id, row]));
    return targets.map(row => ({ ...row, ...(summaryById.get(row.id) || {}) }));
  }, [targets, summary, mode]);

  const uploadTargets = async (rows) => {
    if (!rows.length) return toast.error("No target rows found");
    setUploading(true);
    try {
      const res = await API.post("/bulk-ops/targets", { rows });
      toast.success(`Saved ${res.data.saved || 0} targets`);
      if (res.data.errors) toast.error(`${res.data.errors} rows need correction`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Target upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveManual = () => uploadTargets([manual]);

  const section = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: 18, marginBottom: 14 };
  const label = { display: "block", fontSize: 12, fontWeight: 800, color: "#64748b", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 };
  const input = { width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", font: "inherit", fontSize: 13, outline: "none", boxSizing: "border-box", background: "#fff" };

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 20, fontWeight: 900, color: "#1e293b" }}>{canManage ? "Target Centre" : "My Target"}</div>
        <div style={{ fontSize: 13, color: "#64748b", marginTop: 3 }}>
          {canManage ? "Assign employee targets and review team performance." : "View your individual monthly, quarterly, and yearly targets."}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 14 }}>
        {canManage && [
          ["assign", "Assign Target"],
          ["summary", "Target Summary"],
        ].map(([key, title]) => (
          <button key={key} onClick={() => setMode(key)}
            style={{ border: "none", borderRadius: 999, padding: "9px 14px", font: "inherit", fontSize: 12, fontWeight: 800, cursor: "pointer", background: mode === key ? accent : "#f1f5f9", color: mode === key ? "#fff" : "#64748b", whiteSpace: "nowrap" }}>
            {title}
          </button>
        ))}
        <button onClick={() => setMode("targets")}
          style={{ border: "none", borderRadius: 999, padding: "9px 14px", font: "inherit", fontSize: 12, fontWeight: 800, cursor: "pointer", background: mode === "targets" ? accent : "#f1f5f9", color: mode === "targets" ? "#fff" : "#64748b", whiteSpace: "nowrap" }}>
          My Target
        </button>
      </div>

      {canManage && mode === "assign" && (
        <>
          <div style={section}>
            <div style={{ fontSize: 15, fontWeight: 900, color: "#1e293b", marginBottom: 12 }}>Assign one target</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
              <div>
                <label style={label}>Employee</label>
                <select value={manual.employee_email} onChange={e => setManual(m => ({ ...m, employee_email: e.target.value }))} style={input}>
                  {employees.map(emp => <option key={emp.id} value={emp.email}>{emp.name} - {emp.email}</option>)}
                </select>
              </div>
              <div>
                <label style={label}>Year</label>
                <input type="number" value={manual.target_year} onChange={e => setManual(m => ({ ...m, target_year: e.target.value }))} style={input} />
              </div>
              <div>
                <label style={label}>Period Type</label>
                <select value={manual.period_type} onChange={e => setManual(m => ({ ...m, period_type: e.target.value, period_value: e.target.value === "yearly" ? 1 : m.period_value }))} style={input}>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label style={label}>{manual.period_type === "quarterly" ? "Quarter" : manual.period_type === "monthly" ? "Month" : "Period"}</label>
                <input type="number" min="1" max={manual.period_type === "monthly" ? 12 : 4} value={manual.period_value} disabled={manual.period_type === "yearly"} onChange={e => setManual(m => ({ ...m, period_value: e.target.value }))} style={input} />
              </div>
              <div>
                <label style={label}>Institutions to Visit</label>
                <input type="number" value={manual.institution_target} onChange={e => setManual(m => ({ ...m, institution_target: e.target.value }))} style={input} />
              </div>
              <div>
                <label style={label}>Revenue Target</label>
                <input type="number" value={manual.revenue_target} onChange={e => setManual(m => ({ ...m, revenue_target: e.target.value }))} style={input} />
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={label}>Notes</label>
              <input value={manual.notes} onChange={e => setManual(m => ({ ...m, notes: e.target.value }))} style={input} />
            </div>
            <button onClick={saveManual} disabled={uploading || !manual.employee_email}
              style={{ marginTop: 12, width: "100%", border: "none", borderRadius: 12, padding: 13, background: accent, color: "#fff", font: "inherit", fontWeight: 900, cursor: "pointer" }}>
              Save Target
            </button>
          </div>

          <div style={section}>
            <div style={{ fontSize: 15, fontWeight: 900, color: "#1e293b", marginBottom: 6 }}>Bulk target upload</div>
            <textarea value={targetCsv} onChange={e => setTargetCsv(e.target.value)}
              style={{ ...input, minHeight: 160, fontFamily: "Consolas, monospace", lineHeight: 1.5, resize: "vertical" }} />
            <button onClick={() => uploadTargets(parseCsv(targetCsv))} disabled={uploading}
              style={{ marginTop: 12, width: "100%", border: "none", borderRadius: 12, padding: 13, background: "#0f172a", color: "#fff", font: "inherit", fontWeight: 900, cursor: "pointer" }}>
              {uploading ? "Uploading..." : "Upload Target CSV"}
            </button>
          </div>
        </>
      )}

      {(mode === "targets" || (canManage && mode === "summary")) && (
        <div style={section}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 900, color: "#1e293b" }}>{mode === "summary" ? "Team Target Summary" : "My Target Progress"}</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>Actuals are calculated from visits and converted revenue.</div>
            </div>
            <button onClick={load} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "8px 12px", background: "#fff", font: "inherit", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>Refresh</button>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>Loading targets...</div>
          ) : targetRows.length === 0 ? (
            <div style={{ textAlign: "center", padding: 34, color: "#94a3b8" }}>No targets assigned yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {targetRows.map(row => {
                const instPct = row.institution_target > 0 ? Math.min(100, Math.round((row.actual_institutions || 0) / row.institution_target * 100)) : 0;
                const revPct = row.revenue_target > 0 ? Math.min(100, Math.round((row.actual_revenue || 0) / row.revenue_target * 100)) : 0;
                return (
                  <div key={row.id} style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: 14, background: "#f8fafc" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 900, color: "#1e293b" }}>{row.employee_name || "My Target"}</div>
                        <div style={{ fontSize: 11, color: "#64748b" }}>{periodLabel(row)} {row.notes ? `- ${row.notes}` : ""}</div>
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 900, color: accent, background: "#fff", borderRadius: 999, padding: "4px 9px", alignSelf: "flex-start" }}>{row.period_type}</div>
                    </div>
                    <ProgressLine label="Institutions" actual={row.actual_institutions || 0} target={row.institution_target || 0} percent={instPct} accent={accent} />
                    <ProgressLine label="Revenue" actual={`Rs. ${money(row.actual_revenue)}`} target={`Rs. ${money(row.revenue_target)}`} percent={revPct} accent="#16a34a" />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProgressLine({ label, actual, target, percent, accent }) {
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#475569", fontWeight: 800, marginBottom: 6 }}>
        <span>{label}</span>
        <span>{actual} / {target}</span>
      </div>
      <div style={{ height: 9, background: "#e2e8f0", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${percent}%`, height: "100%", background: accent, borderRadius: 999 }} />
      </div>
    </div>
  );
}
